import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Image, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { CustomForm, CustomModal } from "../../components";
import {
  useCreateSportMutation,
  useDeleteSportMutation,
  useFetchAllSportsQuery,
  useUpdateSportMutation,
} from "../../store/api/apiSlice";
import { Button as CustomBtn, Input } from "../../ui";

const SportsManagement = () => {
  const {
    data: sportsData,
    isLoading,
    error,
    refetch,
  } = useFetchAllSportsQuery();

  const [updateSport] = useUpdateSportMutation();
  const [deleteSport] = useDeleteSportMutation();
  const [createSport] = useCreateSportMutation();
  const [saveButton, setSaveButton] = useState(false);

  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedSport, setSelectedSport] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [isViewModalOpen, setViewModalOpen] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [name, setName] = useState("");
  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [description, setDescription] = useState("");
  const sportNameRef = useRef();
  const descriptionRef = useRef();
  const [loadingError, setLoadingError] = useState("");
  const baseUrl = process.env.REACT_APP_SERVER_URL;
  const secretKey = process.env.REACT_APP_SECRET_KEY;

  const openModal = (sport = null) => {
    setSelectedSport(sport);
    setModalOpen(true);

    if (sport) {
      setName(sport.name);
      setDescription(sport.description);
      setImageFile(sport.sportsFile);
      setImagePreview(null); // Reset preview for existing sport
    } else {
      setName("");
      setDescription("");
      setImageFile(null);
      setImagePreview(null); // Reset preview for new sport
    }

    setIsCreating(!sport);
  };

  const closeModal = () => {
    setSaveButton(false);
    setModalOpen(false);
    setSelectedSport(null);
    setIsCreating(false);
    // Reset all image-related state
    setImageFile(null);
    setImagePreview(null);
    setLoadingError("");
    // Reset form fields
    setName("");
    setDescription("");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "name") {
      setName(value);
    } else if (name === "description") {
      setDescription(value);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      setLoadingError("");
      setImageFile(file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    setSaveButton(true);
    e.preventDefault();

    if (!name) {
      setSaveButton(false);
      toast.error("Name is required.");
      return;
    }

    const formData = new FormData();
    formData.append("name", name);
    formData.append("description", description);

    if (imageFile) {
      formData.append("sportsFile", imageFile);
    } else if (selectedSport?.sportsFile) {
      formData.append("sportsFile", selectedSport.sportsFile);
    }

    const xAuthToken = JSON.parse(localStorage.getItem("token"))?.token;

    try {
      if (isCreating) {
        const response = await fetch(`${baseUrl}/api/sports/create`, {
          method: "POST",
          headers: {
            "secret-key": secretKey,
            "x-auth-token": xAuthToken,
          },
          body: formData,
        });

        if (response.ok) {
          const data = await response.json();
          setSaveButton(false);
          toast.success("Sport created successfully!");
        } else {
          const errorData = await response.json();
          setSaveButton(false);
          toast.error(`Error while saving sport: ${errorData.error}`);
        }
      } else {
        const id = selectedSport._id;

        const response = await fetch(`${baseUrl}/api/sports/update/${id}`, {
          method: "PUT",
          headers: {
            "secret-key": secretKey,
            "x-auth-token": xAuthToken,
          },
          body: formData,
        });

        if (response.ok) {
          const data = await response.json();
          setSaveButton(false);
          toast.success("Sport updated successfully!");

          setSelectedSport(data.sport);
          setName(data.sport.name);
          setDescription(data.sport.description);
          setImageFile(data.sport.sportsFile);
        } else {
          const errorData = await response.json();
          setSaveButton(false);
          toast.error(`Error while updating sport: ${errorData.error}`);
        }
      }

      refetch();
      closeModal();
    } catch (error) {
      setSaveButton(false);
      toast.error("Error while saving sport");
    }
  };

  const handleDelete = async (e, id) => {
    e.preventDefault();
    try {
      await deleteSport(id).unwrap();
      refetch();
      toast.success("Sport deleted successfully");
    } catch (error) {
      toast.error("Error deleting sport:", error);
    }
  };

  const filteredSports =
    sportsData?.sports
      ?.filter((sport) =>
        `${sport.name} ${sport.description}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      )
      ?.sort((a, b) => {
        if (sortOrder === "asc") {
          return a.name.localeCompare(b.name);
        } else {
          return b.name.localeCompare(a.name);
        }
      }) || [];
  const paginatedUsers = filteredSports.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const totalPages = Math.ceil(filteredSports.length / itemsPerPage);

  const openViewModal = async (id) => {
    const xAuthToken =
      JSON.parse(localStorage.getItem("token") || "{}")?.token || "";
    const headers = {
      "secret-key": process.env.REACT_APP_SECRET_KEY || "",
      "x-auth-token": xAuthToken,
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/sports/get-single/${id}`,
        {
          headers,
        }
      );
      if (response.ok) {
        const data = await response.json();
        setSelectedSport(data.sport);
        setViewModalOpen(true);
      } else {
        toast.error("Failed to fetch sport details.");
      }
    } catch (error) {
      toast.error("An error occurred while fetching sport details.");
    }
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    setSelectedSport(null);
  };

  if (isLoading) {
    return <div className="loading-container">Loading...</div>;
  }

  if (error) {
    return <h1>Error loading sports.</h1>;
  }

  return (
    <>
      <div className="d-flex justify-content-between pb-4 gap-4 pt-4">
        <Button
          variant="outline-secondary"
          className="sort-button"
          onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
          style={{ maxWidth: "250px", width: "100%" }}
        >
          {sortOrder === "asc" ? "Sort by Descending" : "Sort by Ascending"}
        </Button>
        <Form.Control
          type="text"
          placeholder="Search by name or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <CustomBtn onClick={() => openModal()}>Create</CustomBtn>
      </div>
      <div className="table-container">
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>Name</th>
              <th>image</th>
              <th>Description</th>

              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedUsers.map((data) => (
              <tr
                key={data._id}
                onClick={() => openViewModal(data._id)}
                style={{ cursor: "pointer" }}
              >
                <td>{data.name}</td>
                <td>
                  <img
                    src={`${baseUrl}/${data.sportsFile}`}
                    alt={data.name}
                    style={{ width: "40px", height: "40px" }}
                  />
                </td>
                <td>{data.description}</td>

                <td>
                  <div
                    style={{
                      display: "flex",
                      gap: "5px",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      className="Update-delete-icon"
                      onClick={(event) => {
                        event.stopPropagation();
                        openModal(data);
                      }}
                      width="30"
                      height="30"
                      src="https://img.icons8.com/ios-glyphs/30/create-new.png"
                      alt="create-new"
                    />

                    <img
                      className="Update-delete-icon"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleDelete(event, data._id);
                      }}
                      width="30"
                      height="30"
                      src="https://img.icons8.com/ios-glyphs/30/filled-trash.png"
                      alt="filled-trash"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <div className="d-flex justify-content-center mt-3">
          {Array.from({ length: totalPages }, (_, index) => (
            <Button
              key={index}
              variant={
                currentPage === index + 1 ? "primary" : "outline-primary"
              }
              onClick={() => setCurrentPage(index + 1)}
              className="mx-1"
            >
              {index + 1}
            </Button>
          ))}
        </div>

        {isModalOpen && (
          <CustomModal isOpen={isModalOpen} onClose={closeModal}>
            <h3 className="modalHeading borderb">
              {isCreating ? "Create Sport" : "Update Sport"}
            </h3>
            <div className="inviteForm">
              <CustomForm onSubmit={handleSubmit} className="col-10 mx-auto">
                <div
                  className="d-flex flex-column row"
                  style={{ marginTop: "10px" }}
                >
                  <p className="fieldWrapper col-12">
                    <Input
                      label="Name"
                      name="name"
                      type="text"
                      value={name}
                      onChange={handleInputChange}
                    />
                  </p>
                  <p className="fieldWrapper col-12">
                    <Input
                      label="Description"
                      name="description"
                      type="text"
                      value={description}
                      onChange={handleInputChange}
                    />
                  </p>

                  <div
                    className="d-flex flex-column gap-2"
                    style={{ maxWidth: "300px" }}
                  >
                    {/* Preview Section */}
                    <div className="border rounded p-1 text-center bg-light">
                      {imagePreview ? (
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="img-fluid rounded"
                          style={{ maxHeight: "120px" }}
                        />
                      ) : selectedSport?.sportsFile ? (
                        <img
                          src={`https://sch-ws.com/${selectedSport.sportsFile}`}
                          alt={selectedSport.name}
                          className="img-fluid rounded"
                          style={{ maxHeight: "120px" }}
                        />
                      ) : (
                        <div className="text-muted py-2 small">
                          No image selected
                        </div>
                      )}
                    </div>

                    {/* Upload Section */}
                    <div>
                      <input
                        type="file"
                        id="imageUpload"
                        accept="image/jpeg, image/png"
                        onChange={handleFileChange}
                        className={`form-control form-control-sm ${
                          error ? "is-invalid" : ""
                        }`}
                      />
                      <div className="text-muted small mt-1">JPG/PNG only</div>
                      {loadingError && (
                        <div className="invalid-feedback d-block">
                          {loadingError}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="btnPosition_college">
                  <Button variant="secondary" onClick={closeModal}>
                    Cancel
                  </Button>
                  <CustomBtn loading={saveButton} type="submit">
                    Save
                  </CustomBtn>
                </div>
              </CustomForm>
            </div>
          </CustomModal>
        )}

        {isViewModalOpen && (
          <CustomModal isOpen={isViewModalOpen} onClose={closeViewModal}>
            <div style={{ maxWidth: "600px", padding: "20px" }}>
              <h3>Sport Details</h3>
              <p>
                <strong>Name:</strong> {selectedSport?.name}
              </p>
              <p>
                <strong>Description:</strong> {selectedSport?.description}
              </p>
              <p>
                <strong>Image:</strong>{" "}
                <Image
                  src={`${baseUrl}/${selectedSport.sportsFile}`}
                  alt={selectedSport?.name}
                  width="50"
                  height="50"
                  style={{ marginBottom: "10px" }}
                  rounded
                />
              </p>
            </div>
          </CustomModal>
        )}
      </div>
    </>
  );
};

export default SportsManagement;
